import type { BeliefBase, BeliefCounts } from '../../services/api'

/**
 * A belief's id, named as its subject stores it: `hypothesisId` on a person, `beliefId` on a
 * product (`toBelief` in `knowledge-api/src/reads.js`). ⚠️ Never read one off the other — the
 * verdict a client sends back names the same field the shared settle selects on.
 */
export function beliefIdOf(b: BeliefBase): string {
  const r = b as BeliefBase & { hypothesisId?: string; beliefId?: string }
  return r.hypothesisId ?? r.beliefId ?? ''
}

/**
 * Move the belief counts for a verdict that has just come back — or leave them alone.
 *
 * ⚠️ **This arithmetic has failed in BOTH directions, one review round apart. Read both
 * before changing it.**
 *
 * The counts are the SERVER's numbers, moved by one — never re-derived from `beliefs`,
 * which only ever holds the statuses the current view shows. The question is only whether
 * the numbers in hand have already had this write applied to them.
 *
 *  - **Over-count (round 3).** Moving them unconditionally double-counted when a read
 *    landed between the POST and its response and came back post-write: the refuted total,
 *    and "(N you marked wrong)", one too high.
 *  - **Under-count (round 4), caused by the fix for the first.** Gating on "is this still
 *    the read the verdict was given against" assumed any interleaved read already included
 *    the write. It does not: a read that reaches the server BEFORE the write commits comes
 *    back with the row still `active` and PRE-write counts. The gate then skipped a move
 *    that was owed, and the totals were one too few. That assumption was written into the
 *    comment as its justification, which is what made it look correct.
 *
 * So the question is answered from STATE, not from timing — it does not matter which
 * request the server saw first. **The status the SNAPSHOT holds for this row is the whole
 * answer**, because the counts and the row came from the same read:
 *
 *  - It holds an older status → those counts predate the write, so move one across, FROM
 *    the status it holds. Not from the status the verdict was given against: those two
 *    differ precisely in the race above, and the counts belong to the snapshot.
 *  - It already holds `next.status` → that read was served after the write (or the write
 *    was a replay that changed nothing), so the count is already where it belongs and
 *    `from === to` stops the arithmetic on its own.
 *  - It does not hold the row at all → leave them, conservatively. A pre-write read always
 *    shows the row as `active` or `confirmed`, both of which the default view renders, so
 *    absence means it has already moved to a status this view hides — which only a
 *    post-write read produces. Inventing a move for a row we cannot see would be a guess.
 *
 * ⚠️ `updatedAt` is deliberately NOT part of this test, though it is the concurrency token
 * everywhere else in this file. A row whose stamp moved but whose status did not is a
 * `from === to` move, which is arithmetically nothing — so comparing stamps would add a
 * condition no behaviour can distinguish, and a reader would reasonably assume it guarded
 * something. It does not.
 *
 * ⚠️ It lives in its own module so it can be driven directly, because the cases that matter
 * most are the ones the screen cannot reach: the only count the UI renders is
 * `refuted + expired`, and a refuted row is hidden in the default view, so "the snapshot
 * already holds the settled row" is unobservable through it. `BeliefRows` calls this
 * function and no other, for people and products alike (MAR-326) — there is no second copy
 * of this arithmetic to drift from.
 *
 * @param counts the counts as read from the server
 * @param held every belief that same read returned
 * @param next the belief as the verdict route just returned it
 * @param idOf the subject's id field — `hypothesisId` or `beliefId`, never a mix
 */
export function applyVerdictToCounts<B extends BeliefBase>(
  counts: BeliefCounts,
  held: readonly B[],
  next: B,
  idOf: (b: B) => string = beliefIdOf
): BeliefCounts {
  const id = idOf(next)
  // An id-less row matches nothing honestly — `'' === ''` would pair it with another one.
  if (!id) return counts
  const mine = held.find((b) => idOf(b) === id)
  if (!mine) return counts

  const from = mine.status
  const to = next.status
  if (!from || !to || !(from in counts) || !(to in counts)) return counts
  // ⚠️ Its own return, not a clause in the guard above, because this is the case the whole
  // function exists to get right: the snapshot already holds the settled row, so there is
  // nothing to move. It is not merely arithmetically inert — `Math.max(0, …)` below floors
  // the decrement, so a same-status move against a count of 0 would ADD one out of nowhere.
  if (from === to) return counts

  const moved: BeliefCounts = { ...counts }
  moved[from as keyof BeliefCounts] = Math.max(0, moved[from as keyof BeliefCounts] - 1)
  moved[to as keyof BeliefCounts] += 1
  return moved
}
